import { Injectable, NgZone, computed, inject, signal } from '@angular/core';
import { HttpClient, HttpEventType, HttpRequest } from '@angular/common/http';
import { firstValueFrom, timeout } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../auth/services/auth.service';
import { EventoAuditoria } from '../core/models/auditoria.model';
import { KvEntity } from '../core/models/asistencia.model';

export interface LoteOutboxMml {
  id: string;
  delta: Record<string, unknown[]>;
  nuevosEventosAuditoria: EventoAuditoria[];
  intentos: number;
  creadoEn: string;
  actualizadoEn: string;
  ultimoError?: string;
}

export interface EvidenciaPendienteMml {
  sesionId: string;
  nombreArchivo: string;
  tipoMime: string;
  tamanoBytes: number;
  blob?: Blob;
  arrayBuffer?: ArrayBuffer;
  motivo?: string;
  creadoEn: string;
  intentos: number;
  ultimoError?: string;
}

export interface ResumenItemColaUi {
  id: string;
  tipo: 'DATOS' | 'FOTO_S3';
  sesionId?: string;
  descripcion: string;
  intentos: number;
  actualizadoEn: string;
  ultimoError?: string;
}

export type EstadoSincronizacionUi =
  | 'AL_DIA'
  | 'SINCRONIZANDO'
  | 'SIN_CONEXION'
  | 'REINTENTANDO_CON_ERROR';

export interface SnapshotServidorReconciliacion {
  sesiones?: Array<Record<string, any>>;
  asistencias?: Array<Record<string, any>>;
  participantes?: Array<Record<string, any>>;
  inscripciones?: Array<Record<string, any>>;
  solicitudes?: Array<Record<string, any>>;
  alertas?: Array<Record<string, any>>;
  auditoria?: Array<{ id?: string; timestamp?: string }>;
}

function tsIsoMs(iso: unknown): number {
  if (typeof iso !== 'string' || !iso) return 0;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : 0;
}

const IDB_NAME = 'mj_asistencia_mml_idb';
const IDB_VERSION = 1;
const STORE_SNAPSHOT = 'snapshot';
const STORE_OUTBOX = 'outbox';
const STORE_EVIDENCIAS = 'evidencias_blobs';
const LOTE_ACTIVO_ID = 'lote-activo-mml';
const OUTBOX_STORAGE_KEY = 'mj_asistencia_mml_outbox_v1';

function claveEntidad(tabla: string, item: unknown): string {
  const obj = (item ?? {}) as Record<string, unknown>;
  if (tabla === 'preferencias') return `${obj['usuarioId']}|${obj['tipo']}`;
  if (tabla === 'plantillas') return `${obj['id']}|${obj['version']}`;
  if (tabla === 'kv') return String(obj['clave']);
  if (tabla === 'alertas') return String(obj['dedupKey'] || obj['id'] || JSON.stringify(obj));
  if (tabla === 'correos' || tabla === 'correosSalida') return String(obj['idempotencyKey'] || obj['id'] || JSON.stringify(obj));
  return String(obj['id'] ?? JSON.stringify(obj));
}

function fusionarFilasTabla(tabla: string, previas: unknown[], nuevas: unknown[]): unknown[] {
  const mapa = new Map<string, unknown>();
  for (const item of previas) {
    mapa.set(claveEntidad(tabla, item), item);
  }
  for (const item of nuevas) {
    mapa.set(claveEntidad(tabla, item), item);
  }
  return Array.from(mapa.values());
}

/**
 * Servicio de Cola Persistente (Outbox) + Almacenamiento Offline en IndexedDB para Asistencia MML.
 *
 * Garantías:
 * 1. Fusión inteligente (Coalescing): si el usuario guarda varias veces la misma clase o realiza
 *    varios cambios mientras no hay internet o hay un intento en curso, fusiona las entidades por
 *    su clave única en un solo lote consolidado (evita saturar el servidor con peticiones redundantes).
 * 2. Verificación estricta (ACK): ningún cambio ni foto se retira de la cola hasta que el servidor
 *    responde HTTP 200 con `{ ok: true, persistidoEnBd: true }` tras hacer COMMIT en PostgreSQL.
 * 3. Cero spam sin red + Backoff exponencial: si `navigator.onLine === false`, pausa por completo
 *    las peticiones de red. Si la red es intermitente, reintenta con backoff exponencial (4s -> 8s -> 16s -> 30s -> 60s).
 * 4. Fotos de evidencia en IndexedDB: si el mentor selecciona la foto con el grupo sin internet o
 *    falla la subida a S3, los bytes (ArrayBuffer) se conservan en IndexedDB (compatible con iOS/iPadOS WebKit)
 *    y nunca bloquean la subida de las listas de asistencia.
 */
@Injectable({ providedIn: 'root' })
export class ColaSincronizacionMmlService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly ngZone = inject(NgZone);
  private readonly apiUrl = environment.URL_SERVER.endsWith('/')
    ? environment.URL_SERVER
    : `${environment.URL_SERVER}/`;

  private dbPromise: Promise<IDBDatabase | null> | null = null;
  private readonly hidratacionInicialPromise: Promise<void>;
  private loteActual: LoteOutboxMml | null = this.cargarOutboxDesdeLocalStorage();
  private evidenciasMap = new Map<string, EvidenciaPendienteMml>();
  private urlsLocalesEvidencia = new Map<string, string>();
  private requiereReconciliacionPrevia = Boolean(this.loteActual);

  private flushEnCurso = false;
  private necesitaNuevoFlush = false;
  private retryTimerId: ReturnType<typeof setTimeout> | null = null;
  private countdownTimerId: ReturnType<typeof setInterval> | null = null;

  private onEvidenciaConfirmadaCallback?: (info: {
    sesionId: string;
    evidenciaUrl: string;
    evidenciaNombre: string;
    tamanoBytes: number;
    motivo?: string;
  }) => Promise<void>;

  private onSincronizacionExitosaCallback?: (serverTimestamp?: string) => void;

  // --- Signals reactivos para la interfaz de usuario ---
  readonly enLinea = signal<boolean>(typeof navigator !== 'undefined' ? navigator.onLine : true);
  readonly sincronizando = signal<boolean>(false);
  readonly ultimoError = signal<string | null>(null);
  readonly segundosParaReintento = signal<number>(0);
  readonly ultimaSincronizacionExitosaIso = signal<string | null>(null);
  readonly avisosFotoDescartadaPorSesion = signal<Record<string, string>>({});
  readonly progresoFoto = signal<{
    sesionId: string;
    porcentaje: number;
    nombreArchivo: string;
    tamanoBytes: number;
    etapa: string;
  } | null>(null);

  private readonly versionCola = signal<number>(0);

  /** Conjunto de IDs de sesión que tienen asistencias o datos de sesión pendientes en la cola */
  readonly sesionesPendientesIds = computed<Set<string>>(() => {
    this.versionCola();
    const set = new Set<string>();
    const lote = this.loteActual;
    if (lote) {
      const sesiones = (lote.delta['sesiones'] ?? []) as Array<{ id?: string }>;
      for (const s of sesiones) {
        if (s?.id) set.add(s.id);
      }
      const asistencias = (lote.delta['asistencias'] ?? []) as Array<{ sesionId?: string }>;
      for (const a of asistencias) {
        if (a?.sesionId) set.add(a.sesionId);
      }
    }
    for (const sesionId of this.evidenciasMap.keys()) {
      set.add(sesionId);
    }
    return set;
  });

  /** Conjunto de IDs de sesión que tienen una foto de evidencia pendiente de subir a S3 */
  readonly fotosPendientesIds = computed<Set<string>>(() => {
    this.versionCola();
    return new Set(this.evidenciasMap.keys());
  });

  /** Detalle legible de los elementos actualmente en cola para mostrar al usuario */
  readonly itemsEnColaUi = computed<ResumenItemColaUi[]>(() => {
    this.versionCola();
    const items: ResumenItemColaUi[] = [];

    const lote = this.loteActual;
    if (lote) {
      const partes: string[] = [];
      const nSes = (lote.delta['sesiones'] ?? []).length;
      const nAsis = (lote.delta['asistencias'] ?? []).length;
      const nGrupos = (lote.delta['grupos'] ?? []).length;
      const nModulos = (lote.delta['modulos'] ?? []).length;
      const nInsc = (lote.delta['inscripciones'] ?? []).length;
      const nPart = (lote.delta['participantes'] ?? []).length;

      if (nAsis > 0) partes.push(`${nAsis} registro(s) de asistencia`);
      if (nSes > 0) partes.push(`${nSes} clase(s)`);
      if (nGrupos > 0) partes.push(`${nGrupos} grupo(s)`);
      if (nModulos > 0) partes.push(`${nModulos} módulo(s)`);
      if (nInsc > 0) partes.push(`${nInsc} inscripción(es)`);
      if (nPart > 0) partes.push(`${nPart} participante(s)`);

      const ultimoEvento = lote.nuevosEventosAuditoria[lote.nuevosEventosAuditoria.length - 1];
      const descripcion = ultimoEvento?.resumen
        ? ultimoEvento.resumen
        : partes.length > 0
          ? `Cambios pendientes: ${partes.join(', ')}`
          : 'Actualización pendiente de confirmar en el servidor';

      items.push({
        id: lote.id,
        tipo: 'DATOS',
        sesionId: ultimoEvento?.contexto?.sesionId,
        descripcion,
        intentos: lote.intentos,
        actualizadoEn: lote.actualizadoEn,
        ultimoError: lote.ultimoError
      });
    }

    for (const ev of this.evidenciasMap.values()) {
      items.push({
        id: `foto-${ev.sesionId}`,
        tipo: 'FOTO_S3',
        sesionId: ev.sesionId,
        descripcion: `Foto con el grupo «${ev.nombreArchivo}» (${Math.max(1, Math.round(ev.tamanoBytes / 1024))} KB)`,
        intentos: ev.intentos,
        actualizadoEn: ev.creadoEn,
        ultimoError: ev.ultimoError
      });
    }

    return items;
  });

  /** Cantidad total de paquetes pendientes por confirmar en el servidor */
  readonly pendientesCount = computed<number>(() => this.itemsEnColaUi().length);

  /** Estado general de sincronización para barras de aviso y badges */
  readonly estadoUi = computed<EstadoSincronizacionUi>(() => {
    const pendientes = this.pendientesCount();
    const online = this.enLinea();
    if (!online) return 'SIN_CONEXION';
    if (this.sincronizando()) return 'SINCRONIZANDO';
    if (pendientes > 0 && this.ultimoError()) return 'REINTENTANDO_CON_ERROR';
    if (pendientes > 0) return 'SINCRONIZANDO';
    return 'AL_DIA';
  });

  constructor() {
    this.hidratacionInicialPromise = this.inicializarDesdeIndexedDb();

    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        this.ngZone.run(() => {
          this.enLinea.set(true);
          this.ultimoError.set(null);
          this.limpiarTimersReintento();
          if (this.tienePendientes()) {
            this.requiereReconciliacionPrevia = true;
          }
          void this.flush();
        });
      });

      window.addEventListener('offline', () => {
        this.ngZone.run(() => {
          this.enLinea.set(false);
          this.limpiarTimersReintento();
          if (this.tienePendientes()) {
            this.requiereReconciliacionPrevia = true;
          }
        });
      });
    }
  }

  esperarHidratacionInicial(): Promise<void> {
    return this.hidratacionInicialPromise;
  }

  registrarCallbacksRepositorio(cbs: {
    onEvidenciaConfirmada: (info: {
      sesionId: string;
      evidenciaUrl: string;
      evidenciaNombre: string;
      tamanoBytes: number;
      motivo?: string;
    }) => Promise<void>;
    onSincronizacionExitosa?: (serverTimestamp?: string) => void;
  }): void {
    this.onEvidenciaConfirmadaCallback = cbs.onEvidenciaConfirmada;
    this.onSincronizacionExitosaCallback = cbs.onSincronizacionExitosa;
  }

  tienePendientes(): boolean {
    return Boolean(this.loteActual) || this.evidenciasMap.size > 0;
  }

  tieneReintentoProgramado(): boolean {
    return this.retryTimerId !== null || this.flushEnCurso;
  }

  tienePendientesParaSesion(sesionId: string): boolean {
    if (!sesionId) return false;
    return this.sesionesPendientesIds().has(sesionId);
  }

  tieneFotoPendienteParaSesion(sesionId: string): boolean {
    if (!sesionId) return false;
    return this.evidenciasMap.has(sesionId);
  }

  obtenerUrlPreviaEvidenciaPendiente(sesionId: string): string | null {
    return this.urlsLocalesEvidencia.get(sesionId) ?? null;
  }

  obtenerMetaEvidenciaPendiente(sesionId: string): EvidenciaPendienteMml | undefined {
    this.versionCola();
    return this.evidenciasMap.get(sesionId);
  }

  limpiarAvisoFotoDescartada(sesionId: string): void {
    if (!sesionId) return;
    const actual = this.avisosFotoDescartadaPorSesion();
    if (!(sesionId in actual)) return;
    const copia = { ...actual };
    delete copia[sesionId];
    this.avisosFotoDescartadaPorSesion.set(copia);
  }

  private registrarAvisoFotoDescartada(sesionId: string, mensaje: string): void {
    if (!sesionId) return;
    this.avisosFotoDescartadaPorSesion.update(prev => ({
      ...prev,
      [sesionId]: mensaje
    }));
  }

  /**
   * Obtiene las entidades pendientes en el outbox para fusionarlas sobre cualquier lectura del servidor
   * y evitar que un GET borre cambios que aún no han sido confirmados por el backend.
   */
  obtenerDeltaPendiente(): Record<string, unknown[]> | null {
    return this.loteActual ? this.loteActual.delta : null;
  }

  obtenerEventosPendientes(): EventoAuditoria[] {
    return this.loteActual ? this.loteActual.nuevosEventosAuditoria : [];
  }

  // ------------------------------------------------------------------------------------------
  // Almacenamiento de Snapshot Completo en IndexedDB (Soporte Offline de Clases del Día)
  // ------------------------------------------------------------------------------------------
  async guardarSnapshotIndexedDb(estado: unknown): Promise<void> {
    const db = await this.abrirDb();
    if (!db) return;
    return new Promise((resolve) => {
      try {
        const tx = db.transaction(STORE_SNAPSHOT, 'readwrite');
        tx.objectStore(STORE_SNAPSHOT).put({
          id: 'estado_v3',
          actualizadoEn: new Date().toISOString(),
          datos: estado
        });
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      } catch {
        resolve();
      }
    });
  }

  async cargarSnapshotIndexedDb<T>(): Promise<T | null> {
    const db = await this.abrirDb();
    if (!db) return null;
    return new Promise((resolve) => {
      try {
        const tx = db.transaction(STORE_SNAPSHOT, 'readonly');
        const req = tx.objectStore(STORE_SNAPSHOT).get('estado_v3');
        req.onsuccess = () => {
          const val = req.result as { datos?: T } | undefined;
          resolve(val?.datos ?? null);
        };
        req.onerror = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
  }

  // ------------------------------------------------------------------------------------------
  // Encolado con Coalescencia (Fusión de Cambios)
  // ------------------------------------------------------------------------------------------
  encolarDelta(
    delta: Record<string, unknown>,
    nuevosEventosAuditoria: EventoAuditoria[]
  ): void {
    const deltaLimpio: Record<string, unknown[]> = {};
    for (const [k, v] of Object.entries(delta)) {
      if (!Array.isArray(v) || v.length === 0) continue;
      if (k === 'kv') {
        const kvFiltrado = (v as KvEntity[]).filter(item => item.clave !== 'lockProgramador');
        if (kvFiltrado.length > 0) deltaLimpio['kv'] = kvFiltrado;
      } else {
        deltaLimpio[k] = v;
      }
    }

    const hayCambios = Object.keys(deltaLimpio).length > 0 || nuevosEventosAuditoria.length > 0;
    if (!hayCambios) return;

    const ahoraIso = new Date().toISOString();
    if (!this.loteActual) {
      this.loteActual = {
        id: LOTE_ACTIVO_ID,
        delta: deltaLimpio,
        nuevosEventosAuditoria: [...nuevosEventosAuditoria],
        intentos: 0,
        creadoEn: ahoraIso,
        actualizadoEn: ahoraIso
      };
    } else {
      const deltaFusionado: Record<string, unknown[]> = { ...this.loteActual.delta };
      for (const [tabla, filasNuevas] of Object.entries(deltaLimpio)) {
        const previas = deltaFusionado[tabla] ?? [];
        deltaFusionado[tabla] = fusionarFilasTabla(tabla, previas, filasNuevas);
      }
      const eventosMap = new Map<string, EventoAuditoria>();
      for (const ev of this.loteActual.nuevosEventosAuditoria) {
        eventosMap.set(ev.accion === 'EVALUACION_ALERTAS_EJECUTADA' ? 'EVALUACION_ALERTAS_EJECUTADA' : ev.id, ev);
      }
      for (const ev of nuevosEventosAuditoria) {
        eventosMap.set(ev.accion === 'EVALUACION_ALERTAS_EJECUTADA' ? 'EVALUACION_ALERTAS_EJECUTADA' : ev.id, ev);
      }
      this.loteActual = {
        ...this.loteActual,
        delta: deltaFusionado,
        nuevosEventosAuditoria: Array.from(eventosMap.values()),
        actualizadoEn: ahoraIso
      };
    }

    this.persistirOutbox();
    this.notificarCambioCola();

    if (!this.enLinea()) {
      this.requiereReconciliacionPrevia = true;
      return;
    }

    if (this.flushEnCurso) {
      this.necesitaNuevoFlush = true;
    } else {
      void this.flush();
    }
  }

  // ------------------------------------------------------------------------------------------
  // Encolado y Subida Resiliente de Foto con el Grupo (S3 + IndexedDB compatible iOS WebKit)
  // ------------------------------------------------------------------------------------------
  private async leerBytesArchivoSeguro(fuente: Blob, timeoutMs = 8000): Promise<ArrayBuffer | null> {
    if (!fuente || fuente.size <= 0) return null;

    const lecturaPromise = (async (): Promise<ArrayBuffer | null> => {
      if (typeof fuente.arrayBuffer === 'function') {
        try {
          const buf = await fuente.arrayBuffer();
          if (buf && buf.byteLength > 0) return buf;
        } catch {
          // Respaldo con FileReader abajo para versiones de WebKit iOS
        }
      }
      if (typeof FileReader !== 'undefined') {
        return new Promise<ArrayBuffer | null>((resolve) => {
          try {
            const reader = new FileReader();
            reader.onload = () => {
              const res = reader.result;
              resolve(res instanceof ArrayBuffer && res.byteLength > 0 ? res : null);
            };
            reader.onerror = () => resolve(null);
            reader.onabort = () => resolve(null);
            reader.readAsArrayBuffer(fuente);
          } catch {
            resolve(null);
          }
        });
      }
      return null;
    })();

    const timeoutPromise = new Promise<null>((resolve) => {
      setTimeout(() => resolve(null), timeoutMs);
    });

    return Promise.race([lecturaPromise, timeoutPromise]);
  }

  async encolarFotoEvidencia(p: {
    sesionId: string;
    file: File;
    motivo?: string;
  }): Promise<{ estado: 'SUBIDA_OK' | 'ENCOLADA_OFFLINE' | 'ERROR_ARCHIVO'; url?: string; mensaje: string }> {
    this.limpiarAvisoFotoDescartada(p.sesionId);

    // En iOS/iPadOS (Safari y Chrome sobre WebKit), los archivos de cámara/galería usan un handle temporal
    // que se corta si se guarda el objeto File directo en IndexedDB o si el <input> se limpia antes de leerlo.
    // Materializamos los bytes completos en un ArrayBuffer en RAM inmediatamente antes de encolar.
    const arrayBuffer = await this.leerBytesArchivoSeguro(p.file, 10_000);
    if (!arrayBuffer || arrayBuffer.byteLength === 0) {
      return {
        estado: 'ERROR_ARCHIVO',
        mensaje: 'No se pudo leer la imagen desde el dispositivo (el archivo llegó vacío o incompleto). Por favor vuelve a seleccionar o tomar la foto.'
      };
    }

    const tipoMime = p.file.type || 'image/jpeg';
    const blobMemoria = new Blob([arrayBuffer], { type: tipoMime });

    const previaUrl = this.urlsLocalesEvidencia.get(p.sesionId);
    if (previaUrl) {
      URL.revokeObjectURL(previaUrl);
    }
    this.urlsLocalesEvidencia.set(p.sesionId, URL.createObjectURL(blobMemoria));

    const item: EvidenciaPendienteMml = {
      sesionId: p.sesionId,
      nombreArchivo: p.file.name || `evidencia-${p.sesionId}.jpg`,
      tipoMime,
      tamanoBytes: arrayBuffer.byteLength,
      blob: blobMemoria,
      arrayBuffer,
      motivo: p.motivo,
      creadoEn: new Date().toISOString(),
      intentos: 0
    };

    this.evidenciasMap.set(p.sesionId, item);
    await this.guardarEvidenciaEnIndexedDb(item);
    this.notificarCambioCola();

    if (!this.enLinea()) {
      this.requiereReconciliacionPrevia = true;
      return {
        estado: 'ENCOLADA_OFFLINE',
        mensaje: 'Sin conexión a internet: la foto con el grupo quedó guardada en este dispositivo y se subirá automáticamente apenas vuelva la señal.'
      };
    }

    await this.flush();

    const avisoDescartada = this.avisosFotoDescartadaPorSesion()[p.sesionId];
    if (avisoDescartada) {
      return {
        estado: 'ERROR_ARCHIVO',
        mensaje: avisoDescartada
      };
    }

    if (!this.evidenciasMap.has(p.sesionId)) {
      return {
        estado: 'SUBIDA_OK',
        mensaje: 'Foto con el grupo subida y confirmada en el servidor.'
      };
    }

    return {
      estado: 'ENCOLADA_OFFLINE',
      mensaje: 'La señal está inestable: la foto quedó en cola segura en este dispositivo y se reintentará automáticamente hasta confirmar su subida.'
    };
  }

  // ------------------------------------------------------------------------------------------
  // Reconciliación Fila por Fila contra el Servidor (protege cambios subidos en otro equipo)
  // ------------------------------------------------------------------------------------------
  async reconciliarContraEstadoServidor(
    remoto: SnapshotServidorReconciliacion
  ): Promise<{ huboDescartesPorServidorMasReciente: boolean }> {
    let huboDescartes = false;

    const mapaSesiones = new Map<string, Record<string, any>>();
    for (const s of remoto.sesiones ?? []) {
      if (s?.['id']) mapaSesiones.set(String(s['id']), s);
    }

    const mapaAsistencias = new Map<string, Record<string, any>>();
    for (const a of remoto.asistencias ?? []) {
      if (a?.['id']) mapaAsistencias.set(String(a['id']), a);
    }

    const mapaParticipantes = new Map<string, Record<string, any>>();
    for (const p of remoto.participantes ?? []) {
      if (p?.['id']) mapaParticipantes.set(String(p['id']), p);
    }

    const mapaInscripciones = new Map<string, Record<string, any>>();
    for (const i of remoto.inscripciones ?? []) {
      if (i?.['id']) mapaInscripciones.set(String(i['id']), i);
    }

    const mapaSolicitudes = new Map<string, Record<string, any>>();
    for (const sol of remoto.solicitudes ?? []) {
      if (sol?.['id']) mapaSolicitudes.set(String(sol['id']), sol);
    }

    const mapaAlertas = new Map<string, Record<string, any>>();
    for (const al of remoto.alertas ?? []) {
      const k = String(al?.['dedupKey'] || al?.['id'] || '');
      if (k) mapaAlertas.set(k, al);
    }

    const idsAuditoriaRemota = new Set<string>();
    for (const ev of remoto.auditoria ?? []) {
      if (ev?.id) idsAuditoriaRemota.add(String(ev.id));
    }

    const sesionesSuperadasSet = new Set<string>();
    const sesionesConCambiosVigentesSet = new Set<string>();

    // 1. Filtrar fila por fila el lote de datos pendiente:
    //    - Lo que en el servidor ya tiene marca de tiempo igual o más reciente se retira de la cola local.
    //    - Solo lo que sea genuinamente más nuevo en este dispositivo se conserva para subir.
    if (this.loteActual) {
      const deltaOriginal = this.loteActual.delta;
      const deltaReconciliado: Record<string, unknown[]> = {};

      // 1a. Asistencias individuales (asi-{sesionId}-{participanteId})
      const asistenciasLocales = (deltaOriginal['asistencias'] ?? []) as Array<Record<string, any>>;
      if (asistenciasLocales.length > 0) {
        const asistenciasVigentes: Array<Record<string, any>> = [];
        for (const local of asistenciasLocales) {
          const srv = mapaAsistencias.get(String(local['id'] ?? ''));
          const localMs = tsIsoMs(local['actualizadaEn'] || local['registradaEn']);
          const srvMs = srv ? tsIsoMs(srv['actualizadaEn'] || srv['registradaEn']) : 0;

          if (srv && srvMs > 0 && localMs > 0 && srvMs >= localMs) {
            huboDescartes = true;
            if (local['sesionId']) sesionesSuperadasSet.add(String(local['sesionId']));
            continue;
          }

          const ajustada = srv
            ? {
                ...local,
                version: Math.max(Number(local['version'] || 1), Number(srv['version'] || 0) + 1)
              }
            : local;
          asistenciasVigentes.push(ajustada);
          if (local['sesionId']) sesionesConCambiosVigentesSet.add(String(local['sesionId']));
        }
        if (asistenciasVigentes.length > 0) {
          deltaReconciliado['asistencias'] = asistenciasVigentes;
        }
      }

      // 1b. Sesiones (preserva siempre evidenciaUrl ya cargada en el servidor desde otro dispositivo)
      const sesionesLocales = (deltaOriginal['sesiones'] ?? []) as Array<Record<string, any>>;
      if (sesionesLocales.length > 0) {
        const sesionesVigentes: Array<Record<string, any>> = [];
        for (const local of sesionesLocales) {
          const sesId = String(local['id'] ?? '');
          const srv = mapaSesiones.get(sesId);
          if (!srv) {
            sesionesVigentes.push(local);
            if (sesId) sesionesConCambiosVigentesSet.add(sesId);
            continue;
          }

          const localMs = Math.max(
            tsIsoMs(local['tomadaEn']),
            tsIsoMs(local['evidenciaSubidaEn']),
            tsIsoMs(local['cerradaEn'])
          );
          const srvMs = Math.max(
            tsIsoMs(srv['tomadaEn']),
            tsIsoMs(srv['evidenciaSubidaEn']),
            tsIsoMs(srv['cerradaEn'])
          );

          const esCambioEstructural =
            Boolean(local['eliminado']) ||
            local['estado'] === 'CANCELADA' ||
            local['fecha'] !== srv['fecha'] ||
            local['horaInicio'] !== srv['horaInicio'] ||
            local['horaFin'] !== srv['horaFin'] ||
            local['reabiertaHasta'] !== srv['reabiertaHasta'];

          const localTraeFotoMasNueva =
            Boolean(local['evidenciaUrl']) &&
            local['evidenciaUrl'] !== srv['evidenciaUrl'] &&
            tsIsoMs(local['evidenciaSubidaEn']) > tsIsoMs(srv['evidenciaSubidaEn']);

          if (!esCambioEstructural && !localTraeFotoMasNueva && srvMs > 0 && (localMs === 0 || srvMs >= localMs)) {
            huboDescartes = true;
            if (sesId) sesionesSuperadasSet.add(sesId);
            continue;
          }

          let fusionada = local;
          if (
            !local['eliminado'] &&
            srv['evidenciaUrl'] &&
            (!local['evidenciaUrl'] || tsIsoMs(srv['evidenciaSubidaEn']) >= tsIsoMs(local['evidenciaSubidaEn']))
          ) {
            fusionada = {
              ...local,
              evidenciaUrl: srv['evidenciaUrl'],
              evidenciaBlobId: srv['evidenciaBlobId'] || srv['evidenciaUrl'],
              evidenciaNombre: srv['evidenciaNombre'] ?? local['evidenciaNombre'],
              evidenciaSubidaEn: srv['evidenciaSubidaEn'] ?? local['evidenciaSubidaEn']
            };
          }

          sesionesVigentes.push(fusionada);
          if (sesId) sesionesConCambiosVigentesSet.add(sesId);
        }
        if (sesionesVigentes.length > 0) {
          deltaReconciliado['sesiones'] = sesionesVigentes;
        }
      }

      // 1c. Participantes, inscripciones, solicitudes y alertas
      for (const [tabla, filasRaw] of Object.entries(deltaOriginal)) {
        if (tabla === 'asistencias' || tabla === 'sesiones') continue;
        if (!Array.isArray(filasRaw) || filasRaw.length === 0) continue;
        const filas = filasRaw as Array<Record<string, any>>;

        if (tabla === 'participantes') {
          const vigentes = filas.filter((local) => {
            const srv = mapaParticipantes.get(String(local['id'] ?? ''));
            const localMs = tsIsoMs(local['actualizadoEn']);
            const srvMs = srv ? tsIsoMs(srv['actualizadoEn']) : 0;
            if (srv && srvMs > 0 && localMs > 0 && srvMs >= localMs) {
              huboDescartes = true;
              return false;
            }
            return true;
          });
          if (vigentes.length > 0) deltaReconciliado[tabla] = vigentes;
          continue;
        }

        if (tabla === 'inscripciones') {
          const vigentes = filas.filter((local) => {
            const srv = mapaInscripciones.get(String(local['id'] ?? ''));
            const localMs = tsIsoMs(local['finalizadaEn'] || local['creadaEn']);
            const srvMs = srv ? tsIsoMs(srv['finalizadaEn'] || srv['creadaEn']) : 0;
            if (
              srv &&
              srv['estado'] === local['estado'] &&
              srv['grupoId'] === local['grupoId'] &&
              srvMs > 0 &&
              localMs > 0 &&
              srvMs >= localMs
            ) {
              huboDescartes = true;
              return false;
            }
            return true;
          });
          if (vigentes.length > 0) deltaReconciliado[tabla] = vigentes;
          continue;
        }

        if (tabla === 'solicitudes') {
          const vigentes = filas.filter((local) => {
            const srv = mapaSolicitudes.get(String(local['id'] ?? ''));
            const localMs = tsIsoMs(local['resueltaEn'] || local['solicitadaEn']);
            const srvMs = srv ? tsIsoMs(srv['resueltaEn'] || srv['solicitadaEn']) : 0;
            if (srv && srv['estado'] === local['estado'] && srvMs > 0 && localMs > 0 && srvMs >= localMs) {
              huboDescartes = true;
              return false;
            }
            return true;
          });
          if (vigentes.length > 0) deltaReconciliado[tabla] = vigentes;
          continue;
        }

        if (tabla === 'alertas') {
          const vigentes = filas.filter((local) => {
            const sesId = local['contexto']?.['sesionId'] ? String(local['contexto']['sesionId']) : '';
            if (sesId && sesionesSuperadasSet.has(sesId) && !sesionesConCambiosVigentesSet.has(sesId)) {
              huboDescartes = true;
              return false;
            }
            const k = String(local['dedupKey'] || local['id'] || '');
            const srv = mapaAlertas.get(k);
            if (!srv) return true;
            const localMs = Math.max(tsIsoMs(local['atendidaEn']), tsIsoMs(local['leidaEn']), tsIsoMs(local['creadaEn']));
            const srvMs = Math.max(tsIsoMs(srv['atendidaEn']), tsIsoMs(srv['leidaEn']), tsIsoMs(srv['creadaEn']));
            if (
              (srv['estado'] === 'ATENDIDA' && local['estado'] !== 'ATENDIDA') ||
              (srvMs > 0 && localMs > 0 && srvMs >= localMs)
            ) {
              huboDescartes = true;
              return false;
            }
            return true;
          });
          if (vigentes.length > 0) deltaReconciliado[tabla] = vigentes;
          continue;
        }

        deltaReconciliado[tabla] = filas;
      }

      const sinCambiosDatos = Object.keys(deltaReconciliado).length === 0;
      const eventosVigentes = this.loteActual.nuevosEventosAuditoria.filter((ev) => {
        if (idsAuditoriaRemota.has(ev.id)) {
          huboDescartes = true;
          return false;
        }
        const sesId = ev.contexto?.sesionId || (ev.entidad === 'SESION' ? ev.entidadId : undefined);
        if (sesId && sesionesSuperadasSet.has(sesId) && !sesionesConCambiosVigentesSet.has(sesId)) {
          huboDescartes = true;
          return false;
        }
        if (ev.entidad === 'ASISTENCIA' && ev.entidadId) {
          const srvAsi = mapaAsistencias.get(ev.entidadId);
          if (srvAsi && tsIsoMs(srvAsi['actualizadaEn'] || srvAsi['registradaEn']) >= tsIsoMs(ev.timestamp)) {
            huboDescartes = true;
            return false;
          }
        }
        if (sinCambiosDatos && ev.accion === 'EVALUACION_ALERTAS_EJECUTADA') {
          return false;
        }
        return true;
      });

      if (sinCambiosDatos && eventosVigentes.length === 0) {
        this.loteActual = null;
      } else {
        this.loteActual = {
          ...this.loteActual,
          delta: deltaReconciliado,
          nuevosEventosAuditoria: eventosVigentes
        };
      }
      this.persistirOutbox();
    }

    // 2. Reconciliar fotos pendientes en IndexedDB:
    //    Si la sesión ya tiene evidenciaUrl en el servidor subida en igual o mayor fecha (ej. desde PC),
    //    retirar la foto vieja pendiente en este dispositivo sin mostrar error ni reintentar.
    //    Si el usuario eligió «Cambiar foto con el grupo» después (localFotoMs > srvFotoMs), SÍ conserva y sube la nueva.
    for (const [sesionId, foto] of Array.from(this.evidenciasMap.entries())) {
      const srv = mapaSesiones.get(sesionId);
      if (!srv?.['evidenciaUrl']) continue;
      const srvFotoMs = tsIsoMs(srv['evidenciaSubidaEn']) || tsIsoMs(srv['tomadaEn']);
      const localFotoMs = tsIsoMs(foto.creadoEn);
      if (srvFotoMs > 0 && localFotoMs > 0 && srvFotoMs >= localFotoMs) {
        this.evidenciasMap.delete(sesionId);
        const urlLocal = this.urlsLocalesEvidencia.get(sesionId);
        if (urlLocal) {
          URL.revokeObjectURL(urlLocal);
          this.urlsLocalesEvidencia.delete(sesionId);
        }
        await this.eliminarEvidenciaDeIndexedDb(sesionId);
        this.limpiarAvisoFotoDescartada(sesionId);
        huboDescartes = true;
      }
    }

    this.requiereReconciliacionPrevia = false;
    if (!this.tienePendientes()) {
      this.ultimoError.set(null);
      this.limpiarTimersReintento();
    }
    this.notificarCambioCola();
    return { huboDescartesPorServidorMasReciente: huboDescartes };
  }

  private async consultarYReconciliarContraServidor(): Promise<boolean> {
    try {
      const resp = await firstValueFrom(
        this.http
          .get<{ tablasCreadas?: boolean; datos?: SnapshotServidorReconciliacion | null }>(
            `${this.apiUrl}mml-asistencia/estado`,
            { params: { _sync: String(Date.now()) } }
          )
          .pipe(timeout(25_000))
      );
      if (resp?.tablasCreadas && resp.datos) {
        await this.reconciliarContraEstadoServidor(resp.datos);
      } else {
        this.requiereReconciliacionPrevia = false;
      }
      return true;
    } catch {
      return false;
    }
  }

  // ------------------------------------------------------------------------------------------
  // Worker de Sincronización con Verificación Estricta (ACK) y Backoff Exponencial
  // ------------------------------------------------------------------------------------------
  async reintentarAhora(): Promise<void> {
    this.limpiarTimersReintento();
    this.ultimoError.set(null);
    if (this.tienePendientes()) {
      this.requiereReconciliacionPrevia = true;
    }
    await this.flush(true);
  }

  async flush(forzadoManual = false): Promise<boolean> {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      this.enLinea.set(false);
      return false;
    }
    this.enLinea.set(true);

    await this.hidratacionInicialPromise;

    if (!this.tienePendientes()) {
      this.ultimoError.set(null);
      this.limpiarTimersReintento();
      return true;
    }

    if (this.flushEnCurso) {
      this.necesitaNuevoFlush = true;
      return false;
    }

    this.flushEnCurso = true;
    this.sincronizando.set(true);
    this.limpiarTimersReintento();
    if (forzadoManual) {
      this.ultimoError.set(null);
    }

    let todoExitoso = true;
    let huboLoteConfirmado = false;
    let ultimoTimestampConfirmado: string | undefined;

    try {
      // PASO 0: Si la cola proviene de almacenamiento previo, modo offline o reintento,
      // reconciliar primero fila por fila contra el servidor para nunca pisar cambios más recientes hechos en otro equipo.
      if (this.requiereReconciliacionPrevia && this.tienePendientes()) {
        const pudoReconciliar = await this.consultarYReconciliarContraServidor();
        if (!pudoReconciliar) {
          todoExitoso = false;
          this.ultimoError.set(
            'Verificando estado actual con el servidor antes de subir la cola pendiente. Se reintentará automáticamente.'
          );
          const intentosRef = Math.max(1, (this.loteActual?.intentos ?? 0) + 1);
          this.programarReintentoBackoff(intentosRef);
          return false;
        }
        if (!this.tienePendientes()) {
          this.ultimoError.set(null);
          this.limpiarTimersReintento();
          return true;
        }
      }

      // PASO 1 (PRIORIDAD): Enviar primero las listas de asistencia/sesiones/datos a PostgreSQL.
      // Así ninguna foto lenta o truncada en iOS puede bloquear jamás el guardado de la asistencia.
      if (this.loteActual) {
        const resDatos = await this.sincronizarLoteActualInterno();
        if (resDatos.confirmado) {
          huboLoteConfirmado = true;
          ultimoTimestampConfirmado = resDatos.serverTimestamp;
        } else {
          todoExitoso = false;
        }
      }

      // PASO 2: Subir fotos de evidencia pendientes a S3 (/upload-file) de forma independiente.
      let algunaFotoSubida = false;
      const fotosPendientes = Array.from(this.evidenciasMap.values());
      for (const foto of fotosPendientes) {
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
          this.enLinea.set(false);
          todoExitoso = false;
          break;
        }
        const resultadoFoto = await this.subirUnaFotoEvidenciaS3(foto);
        if (resultadoFoto === 'SUBIDA_OK') {
          algunaFotoSubida = true;
        } else if (resultadoFoto === 'REINTENTAR_RED') {
          todoExitoso = false;
        }
        // Si resultadoFoto === 'DESCARTADA_CORRUPTA', el archivo roto ya fue retirado de la cola
        // para cortar el bucle infinito y no marca fallo de red ni agenda nuevos reintentos.
      }

      // PASO 3: Si alguna foto subió a S3 en el Paso 2, su URL quedó encolada en loteActual;
      // la confirmamos de inmediato en PostgreSQL dentro del mismo ciclo.
      if (algunaFotoSubida && this.loteActual && (typeof navigator === 'undefined' || navigator.onLine)) {
        this.necesitaNuevoFlush = false;
        const resAsociacion = await this.sincronizarLoteActualInterno();
        if (resAsociacion.confirmado) {
          huboLoteConfirmado = true;
          ultimoTimestampConfirmado = resAsociacion.serverTimestamp;
        } else {
          todoExitoso = false;
        }
      }

      if (!this.tienePendientes()) {
        // Cola completamente vacía: apagar cualquier error residual y garantizar 0 timers activos
        this.ultimoError.set(null);
        this.limpiarTimersReintento();
      } else if (!todoExitoso) {
        this.requiereReconciliacionPrevia = true;
        const intentosDatos = this.loteActual?.intentos ?? 0;
        let maxIntentosFotos = 0;
        for (const ev of this.evidenciasMap.values()) {
          if (ev.intentos > maxIntentosFotos) maxIntentosFotos = ev.intentos;
        }
        const intentosRef = Math.max(1, intentosDatos, maxIntentosFotos);
        this.programarReintentoBackoff(intentosRef);
      }
    } finally {
      this.flushEnCurso = false;
      this.sincronizando.set(false);
    }

    if (huboLoteConfirmado) {
      this.onSincronizacionExitosaCallback?.(ultimoTimestampConfirmado);
    }

    if (this.necesitaNuevoFlush && todoExitoso && this.enLinea() && this.tienePendientes()) {
      this.necesitaNuevoFlush = false;
      return this.flush();
    }
    this.necesitaNuevoFlush = false;
    return todoExitoso;
  }

  private async sincronizarLoteActualInterno(): Promise<{ confirmado: boolean; serverTimestamp?: string }> {
    if (!this.loteActual) {
      return { confirmado: true };
    }

    const snapshotEnviado = structuredClone(this.loteActual);
    const timestampEnviado = snapshotEnviado.actualizadoEn;

    const exitoLote = await this.enviarLoteAlServidor(snapshotEnviado);
    if (exitoLote.ok) {
      // Verificar si durante el envío el usuario agregó más cambios nuevos al lote
      if (this.loteActual && this.loteActual.actualizadoEn === timestampEnviado) {
        this.loteActual = null;
      } else if (this.loteActual) {
        const deltaRestante: Record<string, unknown[]> = {};
        for (const [tabla, filasActuales] of Object.entries(this.loteActual.delta)) {
          if (!Array.isArray(filasActuales) || filasActuales.length === 0) continue;
          const enviadasTabla = snapshotEnviado.delta[tabla] ?? [];
          const mapaEnviadas = new Map<string, string>();
          for (const itemEnv of enviadasTabla) {
            mapaEnviadas.set(claveEntidad(tabla, itemEnv), JSON.stringify(itemEnv));
          }
          const noConfirmadas = filasActuales.filter(itemAct => {
            const k = claveEntidad(tabla, itemAct);
            const jsonEnv = mapaEnviadas.get(k);
            return jsonEnv === undefined || jsonEnv !== JSON.stringify(itemAct);
          });
          if (noConfirmadas.length > 0) {
            deltaRestante[tabla] = noConfirmadas;
          }
        }
        const idsConfirmados = new Set(snapshotEnviado.nuevosEventosAuditoria.map(e => e.id));
        this.loteActual.delta = deltaRestante;
        this.loteActual = {
          ...this.loteActual,
          nuevosEventosAuditoria: this.loteActual.nuevosEventosAuditoria.filter(e => !idsConfirmados.has(e.id)),
          intentos: 0,
          ultimoError: undefined
        };
        const quedanCambios =
          Object.values(this.loteActual.delta).some(arr => Array.isArray(arr) && arr.length > 0) ||
          this.loteActual.nuevosEventosAuditoria.length > 0;
        if (!quedanCambios) {
          this.loteActual = null;
        }
      }
      this.persistirOutbox();
      if (this.evidenciasMap.size === 0) {
        this.ultimoError.set(null);
      }
      const ahoraConfirmado = exitoLote.serverTimestamp || new Date().toISOString();
      this.ultimaSincronizacionExitosaIso.set(ahoraConfirmado);
      this.notificarCambioCola();
      return { confirmado: true, serverTimestamp: ahoraConfirmado };
    }

    const intentos = (this.loteActual?.intentos ?? 0) + 1;
    const msgError =
      exitoLote.mensajeError ||
      'No se pudo confirmar el guardado en el servidor. Se reintentará automáticamente.';
    if (this.loteActual) {
      this.loteActual = {
        ...this.loteActual,
        intentos,
        ultimoError: msgError
      };
      this.persistirOutbox();
    }
    this.ultimoError.set(msgError);
    this.notificarCambioCola();
    return { confirmado: false };
  }

  private async descartarEvidenciaCorrupta(sesionId: string, mensajeUsuario: string): Promise<void> {
    this.evidenciasMap.delete(sesionId);
    const urlLocal = this.urlsLocalesEvidencia.get(sesionId);
    if (urlLocal) {
      URL.revokeObjectURL(urlLocal);
      this.urlsLocalesEvidencia.delete(sesionId);
    }
    await this.eliminarEvidenciaDeIndexedDb(sesionId);
    this.progresoFoto.set(null);
    this.registrarAvisoFotoDescartada(sesionId, mensajeUsuario);
    this.notificarCambioCola();
  }

  private async subirUnaFotoEvidenciaS3(
    foto: EvidenciaPendienteMml
  ): Promise<'SUBIDA_OK' | 'REINTENTAR_RED' | 'DESCARTADA_CORRUPTA'> {
    // 1. Obtener bytes íntegros en RAM (ArrayBuffer) para evitar que WebKit iOS corte el multipart/form-data
    let arrayBuffer =
      foto.arrayBuffer && foto.arrayBuffer.byteLength > 0 ? foto.arrayBuffer : null;

    if (!arrayBuffer && foto.blob) {
      arrayBuffer = await this.leerBytesArchivoSeguro(foto.blob, 8_000);
    }

    if (
      !arrayBuffer ||
      arrayBuffer.byteLength === 0 ||
      (foto.tamanoBytes > 0 && arrayBuffer.byteLength < foto.tamanoBytes)
    ) {
      await this.descartarEvidenciaCorrupta(
        foto.sesionId,
        `La foto anterior («${foto.nombreArchivo}») quedó incompleta al capturarse en el dispositivo y fue retirada de la cola para no bloquear la clase. Si deseas adjuntarla, vuelve a seleccionarla.`
      );
      return 'DESCARTADA_CORRUPTA';
    }

    const bufferSeguro: ArrayBuffer = arrayBuffer;
    const tipoMime = foto.tipoMime || 'image/jpeg';
    const blobEnvio = new Blob([bufferSeguro], { type: tipoMime });
    const tamanoReal = blobEnvio.size;

    const ext = (foto.nombreArchivo.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
    const nombreUnico = `evidencia-${foto.sesionId}-${Date.now()}.${ext}`;

    const formData = new FormData();
    formData.append('bucket', 'medellinjoven-production');
    formData.append('folder', 'asistencia-mml/evidencias');
    formData.append('file', blobEnvio, nombreUnico);

    this.progresoFoto.set({
      sesionId: foto.sesionId,
      porcentaje: 8,
      nombreArchivo: foto.nombreArchivo,
      tamanoBytes: tamanoReal,
      etapa: 'Subiendo foto con el grupo a la nube…'
    });

    try {
      const req = new HttpRequest('POST', `${this.apiUrl}upload-file`, formData, {
        reportProgress: true
      });

      const urlSubida = await new Promise<string>((resolve, reject) => {
        this.http
          .request<{ success: boolean; url: string; message?: string }>(req)
          .pipe(timeout(45_000))
          .subscribe({
            next: (event) => {
              if (event.type === HttpEventType.UploadProgress) {
                const total = event.total || tamanoReal || 1;
                const pct = Math.min(90, Math.max(10, Math.round((event.loaded / total) * 90)));
                this.progresoFoto.set({
                  sesionId: foto.sesionId,
                  porcentaje: pct,
                  nombreArchivo: foto.nombreArchivo,
                  tamanoBytes: tamanoReal,
                  etapa: 'Subiendo foto con el grupo a la nube…'
                });
              } else if (event.type === HttpEventType.Response) {
                const body = event.body;
                if (body?.success && body.url) {
                  resolve(body.url);
                } else {
                  reject(new Error(body?.message || 'El servidor de archivos no confirmó la recepción de la imagen.'));
                }
              }
            },
            error: (err) => {
              reject(err);
            }
          });
      });

      this.progresoFoto.set({
        sesionId: foto.sesionId,
        porcentaje: 95,
        nombreArchivo: foto.nombreArchivo,
        tamanoBytes: tamanoReal,
        etapa: 'Asociando foto a la clase en la base de datos…'
      });

      const baseS3 = environment.URL_S3.endsWith('/') ? environment.URL_S3 : `${environment.URL_S3}/`;
      const urlCompleta = urlSubida.startsWith('http')
        ? urlSubida
        : `${baseS3}${urlSubida.replace(/^\//, '')}`;

      if (this.onEvidenciaConfirmadaCallback) {
        await this.onEvidenciaConfirmadaCallback({
          sesionId: foto.sesionId,
          evidenciaUrl: urlCompleta,
          evidenciaNombre: foto.nombreArchivo,
          tamanoBytes: tamanoReal,
          motivo: foto.motivo
        });
      }

      // Solo tras haber obtenido la URL de S3 y registrado en el estado local/outbox, retiramos la evidencia pendiente
      this.evidenciasMap.delete(foto.sesionId);
      const urlLocal = this.urlsLocalesEvidencia.get(foto.sesionId);
      if (urlLocal) {
        URL.revokeObjectURL(urlLocal);
        this.urlsLocalesEvidencia.delete(foto.sesionId);
      }
      await this.eliminarEvidenciaDeIndexedDb(foto.sesionId);
      this.limpiarAvisoFotoDescartada(foto.sesionId);
      this.progresoFoto.set(null);
      this.notificarCambioCola();
      return 'SUBIDA_OK';
    } catch (err: any) {
      this.progresoFoto.set(null);
      const st = Number(err?.status ?? 0);
      const detalleErr = String(err?.error?.message || err?.message || '');
      const esFormularioTruncadoOArchivoInvalido =
        st === 400 ||
        st === 413 ||
        st === 415 ||
        st === 422 ||
        /unexpected end of form/i.test(detalleErr);

      // Si el servidor rechaza el archivo con 4xx (ej. "Unexpected end of form" de una foto previa corrupta en iOS),
      // no sirve reintentar el mismo archivo truncado en bucle infinito: se retira de la cola y se avisa al mentor.
      if (esFormularioTruncadoOArchivoInvalido) {
        await this.descartarEvidenciaCorrupta(
          foto.sesionId,
          `La foto anterior («${foto.nombreArchivo}») llegó incompleta desde el navegador y se retiró de la cola para no bloquear la asistencia. Vuelve a seleccionarla si deseas subirla.`
        );
        return 'DESCARTADA_CORRUPTA';
      }

      const intentos = (foto.intentos ?? 0) + 1;
      const esTimeout = err?.name === 'TimeoutError';
      const msg = esTimeout
        ? 'Señal muy débil: la subida de la foto tardó demasiado. La imagen sigue guardada en este dispositivo y se reintentará automáticamente.'
        : (detalleErr || 'No se pudo subir la foto por inestabilidad de red. Se reintentará automáticamente.');
      const actualizada: EvidenciaPendienteMml = {
        ...foto,
        tamanoBytes: tamanoReal,
        blob: blobEnvio,
        arrayBuffer: bufferSeguro,
        intentos,
        ultimoError: msg
      };
      this.evidenciasMap.set(foto.sesionId, actualizada);
      await this.guardarEvidenciaEnIndexedDb(actualizada);
      this.ultimoError.set(msg);
      this.notificarCambioCola();
      return 'REINTENTAR_RED';
    }
  }

  private async enviarLoteAlServidor(lote: LoteOutboxMml): Promise<{
    ok: boolean;
    serverTimestamp?: string;
    mensajeError?: string;
  }> {
    const rutaActual = typeof window !== 'undefined' ? window.location.pathname : '';
    const esRutaPublicaOEstudiante =
      rutaActual.startsWith('/inscripcion-mml') || rutaActual.startsWith('/mi-progreso-mml');
    const urlPublica = `${this.apiUrl}mml-asistencia/sincronizar-inscripcion`;
    const urlAdmin = `${this.apiUrl}mml-asistencia/sincronizar`;
    const usarAdmin = Boolean(this.authService.getToken()) && !esRutaPublicaOEstudiante;

    const body = {
      ...lote.delta,
      nuevosEventosAuditoria: lote.nuevosEventosAuditoria
    };

    try {
      const resp = await firstValueFrom(
        this.http
          .post<{
            ok?: boolean;
            persistidoEnBd?: boolean;
            serverTimestamp?: string;
            message?: string;
          }>(usarAdmin ? urlAdmin : urlPublica, body)
          .pipe(timeout(45_000))
      );

      if (resp && resp.ok === true && resp.persistidoEnBd !== false) {
        return { ok: true, serverTimestamp: resp.serverTimestamp };
      }
      return {
        ok: false,
        mensajeError: resp?.message || 'El servidor recibió la solicitud pero no confirmó el guardado en la base de datos.'
      };
    } catch (err: any) {
      const st = Number(err?.status ?? 0);
      const esTimeout = err?.name === 'TimeoutError';
      // Fallback al endpoint de sincronización si el token expiró temporalmente en segundo plano
      if (usarAdmin && (st === 401 || st === 403 || st === 419 || st === 420)) {
        try {
          const respFallback = await firstValueFrom(
            this.http
              .post<{
                ok?: boolean;
                persistidoEnBd?: boolean;
                serverTimestamp?: string;
                message?: string;
              }>(urlPublica, body)
              .pipe(timeout(45_000))
          );
          if (respFallback && respFallback.ok === true && respFallback.persistidoEnBd !== false) {
            return { ok: true, serverTimestamp: respFallback.serverTimestamp };
          }
        } catch {
          // Continúa al manejo estándar de error abajo
        }
      }

      if (esTimeout) {
        return {
          ok: false,
          mensajeError: 'Tu señal de internet está muy débil y la petición se cortó por tiempo de espera. Tus cambios siguen guardados en la cola y se reintentarán automáticamente.'
        };
      }

      if (st === 0 || (typeof navigator !== 'undefined' && !navigator.onLine)) {
        return {
          ok: false,
          mensajeError: 'Sin conexión estable con el servidor. Tus cambios siguen guardados en este dispositivo y se subirán automáticamente.'
        };
      }
      return {
        ok: false,
        mensajeError: err?.error?.message || `El servidor tardó en responder o devolvió un error (${st || 'red'}). Tus cambios siguen seguros en la cola.`
      };
    }
  }

  private programarReintentoBackoff(intentos: number): void {
    this.limpiarTimersReintento();
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;
    if (!this.tienePendientes()) return;

    // Backoff exponencial controlado: 4s, 8s, 16s, 30s, máx 60s (nunca sobrecarga el servidor)
    const baseSeg = Math.min(60, Math.round(4 * Math.pow(1.8, Math.max(0, intentos - 1))));
    this.segundosParaReintento.set(baseSeg);

    this.ngZone.runOutsideAngular(() => {
      this.countdownTimerId = setInterval(() => {
        const rest = this.segundosParaReintento() - 1;
        if (rest >= 0) {
          this.ngZone.run(() => this.segundosParaReintento.set(rest));
        }
      }, 1000);

      this.retryTimerId = setTimeout(() => {
        this.ngZone.run(() => {
          this.limpiarTimersReintento();
          if (this.enLinea() && this.tienePendientes()) {
            void this.flush();
          }
        });
      }, baseSeg * 1000);
    });
  }

  private limpiarTimersReintento(): void {
    if (this.retryTimerId) {
      clearTimeout(this.retryTimerId);
      this.retryTimerId = null;
    }
    if (this.countdownTimerId) {
      clearInterval(this.countdownTimerId);
      this.countdownTimerId = null;
    }
    this.segundosParaReintento.set(0);
  }

  private notificarCambioCola(): void {
    this.versionCola.update(v => v + 1);
  }

  // ------------------------------------------------------------------------------------------
  // Persistencia de Outbox y Evidencias en localStorage + IndexedDB
  // ------------------------------------------------------------------------------------------
  private cargarOutboxDesdeLocalStorage(): LoteOutboxMml | null {
    if (typeof localStorage === 'undefined') return null;
    try {
      const raw = localStorage.getItem(OUTBOX_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as LoteOutboxMml;
      if (parsed && parsed.delta && typeof parsed.delta === 'object') {
        return parsed;
      }
      return null;
    } catch {
      return null;
    }
  }

  private persistirOutbox(): void {
    if (typeof localStorage !== 'undefined') {
      try {
        if (this.loteActual) {
          localStorage.setItem(OUTBOX_STORAGE_KEY, JSON.stringify(this.loteActual));
        } else {
          localStorage.removeItem(OUTBOX_STORAGE_KEY);
        }
      } catch {
        // Ignorar si localStorage supera cuota; IndexedDB guarda el respaldo completo
      }
    }
    void this.sincronizarOutboxEnIndexedDb();
  }

  private async abrirDb(): Promise<IDBDatabase | null> {
    if (typeof indexedDB === 'undefined') return null;
    if (this.dbPromise) return this.dbPromise;

    this.dbPromise = new Promise((resolve) => {
      try {
        const req = indexedDB.open(IDB_NAME, IDB_VERSION);
        req.onupgradeneeded = () => {
          const db = req.result;
          if (!db.objectStoreNames.contains(STORE_SNAPSHOT)) {
            db.createObjectStore(STORE_SNAPSHOT, { keyPath: 'id' });
          }
          if (!db.objectStoreNames.contains(STORE_OUTBOX)) {
            db.createObjectStore(STORE_OUTBOX, { keyPath: 'id' });
          }
          if (!db.objectStoreNames.contains(STORE_EVIDENCIAS)) {
            db.createObjectStore(STORE_EVIDENCIAS, { keyPath: 'sesionId' });
          }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(null);
      } catch {
        resolve(null);
      }
    });

    return this.dbPromise;
  }

  private async inicializarDesdeIndexedDb(): Promise<void> {
    const db = await this.abrirDb();
    if (!db) return;

    // 1. Recuperar lote outbox si no estaba en localStorage
    await new Promise<void>((resolve) => {
      try {
        const tx = db.transaction(STORE_OUTBOX, 'readonly');
        const req = tx.objectStore(STORE_OUTBOX).get(LOTE_ACTIVO_ID);
        req.onsuccess = () => {
          const guardado = req.result as LoteOutboxMml | undefined;
          if (guardado && !this.loteActual) {
            this.loteActual = guardado;
          }
          resolve();
        };
        req.onerror = () => resolve();
      } catch {
        resolve();
      }
    });

    // 2. Recuperar fotos pendientes de S3 (tanto ArrayBuffer nuevo como Blob previo)
    await new Promise<void>((resolve) => {
      try {
        const tx = db.transaction(STORE_EVIDENCIAS, 'readonly');
        const req = tx.objectStore(STORE_EVIDENCIAS).getAll();
        req.onsuccess = () => {
          const lista = (req.result ?? []) as EvidenciaPendienteMml[];
          for (const item of lista) {
            if (!item?.sesionId) continue;
            if (item.arrayBuffer && item.arrayBuffer.byteLength > 0) {
              const blobReconstruido = new Blob([item.arrayBuffer], {
                type: item.tipoMime || 'image/jpeg'
              });
              const hidratado: EvidenciaPendienteMml = {
                ...item,
                tamanoBytes: item.arrayBuffer.byteLength,
                blob: blobReconstruido
              };
              this.evidenciasMap.set(item.sesionId, hidratado);
              if (!this.urlsLocalesEvidencia.has(item.sesionId)) {
                this.urlsLocalesEvidencia.set(item.sesionId, URL.createObjectURL(blobReconstruido));
              }
            } else if (item.blob) {
              this.evidenciasMap.set(item.sesionId, item);
              if (!this.urlsLocalesEvidencia.has(item.sesionId)) {
                try {
                  this.urlsLocalesEvidencia.set(item.sesionId, URL.createObjectURL(item.blob));
                } catch {
                  // Ignorar si el Blob previo de WebKit ya no permite crear ObjectURL
                }
              }
            }
          }
          resolve();
        };
        req.onerror = () => resolve();
      } catch {
        resolve();
      }
    });

    if (this.tienePendientes()) {
      this.requiereReconciliacionPrevia = true;
    }
    this.notificarCambioCola();
  }

  private async sincronizarOutboxEnIndexedDb(): Promise<void> {
    const db = await this.abrirDb();
    if (!db) return;
    return new Promise((resolve) => {
      try {
        const tx = db.transaction(STORE_OUTBOX, 'readwrite');
        const store = tx.objectStore(STORE_OUTBOX);
        if (this.loteActual) {
          store.put(this.loteActual);
        } else {
          store.delete(LOTE_ACTIVO_ID);
        }
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      } catch {
        resolve();
      }
    });
  }

  private async guardarEvidenciaEnIndexedDb(item: EvidenciaPendienteMml): Promise<void> {
    const db = await this.abrirDb();
    if (!db) return;
    return new Promise((resolve) => {
      try {
        const tx = db.transaction(STORE_EVIDENCIAS, 'readwrite');
        // En iOS/iPadOS WebKit, almacenar un objeto Blob/File en IndexedDB dispara fallos de journal externo;
        // cuando ya disponemos de ArrayBuffer en memoria, persistimos únicamente los bytes puros inline.
        const registroPersistir: EvidenciaPendienteMml =
          item.arrayBuffer && item.arrayBuffer.byteLength > 0
            ? { ...item, blob: undefined }
            : item;
        tx.objectStore(STORE_EVIDENCIAS).put(registroPersistir);
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      } catch {
        resolve();
      }
    });
  }

  private async eliminarEvidenciaDeIndexedDb(sesionId: string): Promise<void> {
    const db = await this.abrirDb();
    if (!db) return;
    return new Promise((resolve) => {
      try {
        const tx = db.transaction(STORE_EVIDENCIAS, 'readwrite');
        tx.objectStore(STORE_EVIDENCIAS).delete(sesionId);
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      } catch {
        resolve();
      }
    });
  }
}
